<template>
  <div class="on-block-panel">
    <div class="on-block-panel__head">
      <div class="on-block-panel__identity">
        <RunNumber :value="runNumber" />
        <span class="on-block-panel__title">
          <span class="on-block-panel__year">{{ vehicle.year }}</span>{{ vehicle.title }}
        </span>
      </div>
      <LightsRow v-model="lights" />
    </div>

    <div class="on-block-panel__body">
      <div class="on-block-panel__spec">
        <AssetDetailsCard :asset="vehicle" :valuation="valuation" fill />
      </div>
      <div class="on-block-panel__bid-history">
        <BidHistoryList :rows="bidHistory" />
      </div>
    </div>
  </div>
</template>

<script setup>
import RunNumber from '../RunNumber/RunNumber.vue';
import LightsRow from '../LightsRow/LightsRow.vue';
import AssetDetailsCard from '../AssetDetailsCard/AssetDetailsCard.vue';
import BidHistoryList from '../BidHistoryList/BidHistoryList.vue';

// The console's left column: what is on the block, and everything that has
// happened to it.
//
// It merged a live vehicle over a mock record field by field, which is where
// the field-name reconciliation used to live. That merge is the application's —
// it is the only thing that knows which feed filled which field — so the rail
// takes one already-resolved vehicle.
//
// The spec card and the bid history sit side by side rather than stacked, matching
// the incumbent console's left panel: a clerk reading a VIN out loud and a
// clerk watching the history are doing two different jobs at the same time.
defineOptions({ name: 'OnBlockPanel' });

defineProps({
  runNumber: { type: [String, Number], default: '' },
  // The resolved vehicle. See AssetDetailsCard for the fields it renders.
  vehicle: { type: Object, required: true },
  // { blackBookValue, marketReportValue, reserveValue }.
  valuation: { type: Object, default: () => ({}) },
  // The lot's ledger, newest first. See BidHistoryList.
  bidHistory: { type: Array, default: () => [] },
});

// The condition lights, editable in place. Bound, the application owns them.
const lights = defineModel({ type: Array, default: () => [] });
</script>

<style scoped>
.on-block-panel {
  /* Grows back to its design width on a large display, but yields to 200px on a
     narrow one so the action grid keeps its width instead of being pushed under
     the increment rail. */
  flex: 1 1;
  min-width: 200px;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: var(--p-on-block-panel-gap);
}

.on-block-panel__head {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--p-on-block-panel-head-background);
  border: var(--p-on-block-panel-head-border-width) solid
    var(--p-on-block-panel-head-border-color);
  border-radius: var(--p-on-block-panel-head-border-radius);
  padding: var(--p-on-block-panel-head-padding);
  gap: var(--p-on-block-panel-head-gap);
}

.on-block-panel__identity {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.on-block-panel__title {
  text-wrap: pretty;
  font-size: var(--p-on-block-panel-title-font-size);
  font-weight: var(--p-on-block-panel-title-font-weight);
  line-height: var(--p-on-block-panel-title-line-height);
  color: var(--p-on-block-panel-title-color);
}

.on-block-panel__year {
  margin-right: 6px;
  font-weight: var(--p-on-block-panel-year-font-weight);
  color: var(--p-on-block-panel-year-color);
}

.on-block-panel__body {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: var(--p-on-block-panel-body-gap);
}

.on-block-panel__spec {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.on-block-panel__bid-history {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
}
</style>
